import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { OrderQueueCard } from "@/components/workshop/OrderQueueCard";
import { DispatchDialog } from "@/components/workshop/DispatchDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import { toast } from "sonner";
import { RefreshCw, Truck, Package, Clock, User, MapPin } from "lucide-react";

interface DispatchOrder {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  itemCount: number;
  items: { id: string; garmentType: string; qty: number; tagCode?: string }[];
  customer: { publicId: string; name: string; phone?: string };
  address: { line1: string; line2?: string; city: string; pincode: string };
  area: { id: string; name: string };
  deliveryShipment?: {
    id: string;
    status: string;
    otp?: string;
    slot: { startAt: string; endAt: string };
    assignedRider?: { name: string; phone?: string };
  };
  pickupShipment?: {
    status: string;
    slot: { startAt: string; endAt: string };
  };
  createdAt: string;
}

const shipmentStatusLabels: Record<string, string> = {
  SCHEDULED: "Scheduled",
  ASSIGNED: "Assigned",
  EN_ROUTE: "En Route",
  OUT_FOR_DELIVERY: "Out for Delivery",
  DELIVERED: "Delivered",
};

const shipmentStatusColors: Record<string, string> = {
  SCHEDULED: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-cyan-100 text-cyan-700",
  EN_ROUTE: "bg-orange-100 text-orange-700",
  OUT_FOR_DELIVERY: "bg-amber-100 text-amber-700",
  DELIVERED: "bg-green-100 text-green-700",
};

export default function DispatchPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<DispatchOrder[]>([]);
  const [dispatchOrder, setDispatchOrder] = useState<DispatchOrder | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    setLoading(true);
    try {
      const response = await fetch(`/api/workshop/orders?stage=dispatch`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateDelivery(slotId: string) {
    if (!dispatchOrder) return;

    const response = await fetch(
      `/api/workshop/orders/${dispatchOrder.id}/dispatch/create-delivery`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deliverySlotId: slotId }),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Failed to create delivery");
    }

    toast.success("Delivery shipment created");
    fetchOrders();
  }

  const readyOrders = orders.filter(
    (o) => o.status === "READY_FOR_DELIVERY" && !o.deliveryShipment
  );

  const scheduledDeliveries = orders.filter(
    (o) => o.deliveryShipment
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dispatch"
        actions={
          <Button variant="outline" onClick={fetchOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Package className="h-5 w-5" />
              Ready for Dispatch
              {readyOrders.length > 0 && (
                <Badge variant="secondary">{readyOrders.length}</Badge>
              )}
            </h2>

            {readyOrders.length === 0 ? (
              <EmptyState
                icon={Truck}
                title="No orders ready for dispatch"
                description="Orders will appear here when production is complete."
              />
            ) : (
              <div className="space-y-4">
                {readyOrders.map((order) => (
                  <OrderQueueCard
                    key={order.id}
                    order={order}
                    action={{
                      label: "Create Delivery",
                      onClick: () => setDispatchOrder(order),
                    }}
                    secondaryAction={{
                      label: "View Order",
                      onClick: () => navigate(`/workshop/orders/${order.id}`),
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Scheduled Deliveries
              {scheduledDeliveries.length > 0 && (
                <Badge variant="secondary">{scheduledDeliveries.length}</Badge>
              )}
            </h2>

            {scheduledDeliveries.length === 0 ? (
              <EmptyState
                icon={Clock}
                title="No scheduled deliveries"
                description="Deliveries will appear here once created."
              />
            ) : (
              <div className="space-y-4">
                {scheduledDeliveries.map((order) => (
                  <Card key={order.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{order.publicId}</CardTitle>
                        {order.deliveryShipment && (
                          <Badge
                            className={
                              shipmentStatusColors[order.deliveryShipment.status] ||
                              "bg-gray-100 text-gray-700"
                            }
                          >
                            {shipmentStatusLabels[order.deliveryShipment.status] ||
                              order.deliveryShipment.status}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{order.customer.name}</span>
                        {order.customer.phone && (
                          <span className="text-muted-foreground">
                            ({order.customer.phone})
                          </span>
                        )}
                      </div>

                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span>
                          {order.address.line1}, {order.address.city}
                        </span>
                      </div>

                      {order.deliveryShipment && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {formatDateLong(order.deliveryShipment.slot.startAt)}{" "}
                            {formatTimeRange(
                              order.deliveryShipment.slot.startAt,
                              order.deliveryShipment.slot.endAt
                            )}
                          </span>
                        </div>
                      )}

                      {order.deliveryShipment?.assignedRider && (
                        <div className="flex items-center gap-2 text-sm">
                          <Truck className="h-4 w-4 text-muted-foreground" />
                          <span>
                            Rider: {order.deliveryShipment.assignedRider.name}
                          </span>
                        </div>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/workshop/orders/${order.id}`)}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {dispatchOrder && (
        <DispatchDialog
          open={!!dispatchOrder}
          onOpenChange={(open) => !open && setDispatchOrder(null)}
          orderId={dispatchOrder.id}
          orderPublicId={dispatchOrder.publicId}
          areaId={dispatchOrder.area.id}
          onConfirm={handleCreateDelivery}
        />
      )}
    </div>
  );
}
